import React, { useState } from 'react';
import { Sparkles, Loader2, RefreshCw, AlertTriangle, Info, CheckCircle } from 'lucide-react';
import { globalApi } from '../../services/crmApi';

interface Action {
  priority: 'high' | 'medium' | 'low';
  action: string;
  reason: string;
}

const PRIORITY_CONFIG: Record<string, { bg: string; text: string; border: string; icon: React.ElementType; label: string }> = {
  high: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: AlertTriangle, label: 'Pilne' },
  medium: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: Info, label: 'Ważne' },
  low: { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200', icon: CheckCircle, label: 'Opcjonalne' },
};

export const NextBestActionWidget: React.FC<{ applicationId: number }> = ({ applicationId }) => {
  const [actions, setActions] = useState<Action[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');

  const analyze = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await globalApi.getNextBestAction(applicationId);
      setActions(data.actions || []);
      setLoaded(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd analizy AI');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 flex items-center justify-between">
        <h3 className="font-bold text-white flex items-center gap-2">
          <Sparkles size={18} />
          AI: Następne działanie
        </h3>
        {loaded && (
          <button
            type="button"
            onClick={analyze}
            disabled={loading}
            className="p-1 rounded hover:bg-white/20 transition-colors"
            title="Odśwież analizę"
          >
            <RefreshCw size={14} className={`text-white ${loading ? 'animate-spin' : ''}`} />
          </button>
        )}
      </div>

      <div className="p-6">
        {!loaded && !loading && (
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-3">
              AI przeanalizuje wniosek i zaproponuje następne kroki
            </p>
            <button
              type="button"
              onClick={analyze}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg text-sm font-medium hover:from-indigo-700 hover:to-purple-700 transition-colors disabled:opacity-50"
            >
              <Sparkles size={16} />
              Analizuj
            </button>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 size={24} className="animate-spin text-indigo-500" />
            <span className="ml-2 text-sm text-gray-500">Analizuję...</span>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">
            {error}
          </div>
        )}

        {loaded && !loading && actions.length > 0 && (
          <div className="space-y-3">
            {actions.map((a, i) => {
              const cfg = PRIORITY_CONFIG[a.priority] || PRIORITY_CONFIG.medium;
              const PIcon = cfg.icon;
              return (
                <div key={i} className={`${cfg.bg} border ${cfg.border} rounded-lg p-3`}>
                  <div className="flex items-center gap-2 mb-1">
                    <PIcon size={14} className={cfg.text} />
                    <span className={`text-xs font-semibold ${cfg.text}`}>{cfg.label}</span>
                  </div>
                  <p className="text-sm font-medium text-gray-800">{a.action}</p>
                  {a.reason && (
                    <p className="text-xs text-gray-500 mt-1">{a.reason}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {loaded && !loading && actions.length === 0 && !error && (
          <p className="text-sm text-gray-400 text-center py-4">Brak sugestii w tej chwili</p>
        )}
      </div>
    </div>
  );
};
